import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiSave, FiUpload, FiTrash2, FiEdit2, FiPlus, 
  FiEye, FiEyeOff, FiImage 
} from 'react-icons/fi';
import { API_BASE_URL } from '../config/config';

const BrandingSettings = () => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    institute: {},
    settings: {}
  });
  const [banners, setBanners] = useState([]);
  const [showBannerModal, setShowBannerModal] = useState(false);
  
  const [bannerForm, setBannerForm] = useState({
    title: '',
    subtitle: '',
    link_url: '',
    button_text: '',
    banner: null
  });
  
  const [bannerPreview, setBannerPreview] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchBanners();
  }, []);

  const fetchSettings = async () => {
    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.get(`${API_BASE_URL}/settings/general`, {
        headers: { 'X-User-ID': userId }
      });
      
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error loading settings');
    }
  };

  const fetchBanners = async () => {
    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.get(`${API_BASE_URL}/banners`, {
        headers: { 'X-User-ID': userId }
      });
      
      if (response.data.success) {
        setBanners(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);
    formData.append('logo_type', 'admin_logo');

    try {
      setLoading(true);
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/settings/upload-logo`,
        formData,
        {
          headers: {
            'X-User-ID': userId,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('Logo uploaded successfully');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Error uploading logo');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerForm({...bannerForm, banner: file});
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBannerUpload = async (e) => {
    e.preventDefault();
    
    if (!bannerForm.banner) {
      toast.error('Please select a banner image');
      return;
    }

    const formData = new FormData();
    formData.append('banner', bannerForm.banner);
    formData.append('title', bannerForm.title);
    formData.append('subtitle', bannerForm.subtitle);
    formData.append('link_url', bannerForm.link_url);
    formData.append('button_text', bannerForm.button_text);

    try {
      setLoading(true);
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/banner/upload`,
        formData,
        {
          headers: {
            'X-User-ID': userId,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success('Banner uploaded successfully');
        setShowBannerModal(false);
        setBannerForm({
          title: '',
          subtitle: '',
          link_url: '',
          button_text: '',
          banner: null
        });
        setBannerPreview(null);
        fetchBanners();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error uploading banner');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.delete(`${API_BASE_URL}/banner/${id}`, {
        headers: { 'X-User-ID': userId }
      });

      if (response.data.success) {
        toast.success('Banner deleted successfully');
        fetchBanners();
      }
    } catch (error) {
      toast.error('Error deleting banner');
    }
  };

  const toggleBannerStatus = async (id) => {
    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/banner/${id}/toggle-status`,
        {},
        { headers: { 'X-User-ID': userId } }
      );

      if (response.data.success) {
        toast.success('Banner status updated');
        fetchBanners();
      }
    } catch (error) {
      toast.error('Error updating banner status');
    }
  };

  const handleUpdateGeneralSettings = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/settings/general`,
        settings.settings,
        { headers: { 'X-User-ID': userId } }
      );

      if (response.data.success) {
        toast.success('Settings updated successfully');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Error updating settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Branding & Appearance
        </h1>
        <p className="text-gray-600">
          Customize your institute's branding, colors, and banners
        </p>
      </div>

      {/* Logo Upload Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          <FiImage className="inline mr-2" />
          Institute Logo
        </h2>
        
        <div className="flex items-center gap-6">
          <div className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
            {settings.settings?.admin_logo ? (
              <img 
                src={settings.settings.admin_logo} 
                alt="Institute Logo" 
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="text-center text-gray-400">
                <FiImage size={48} className="mx-auto mb-2" />
                <p>No logo uploaded</p>
              </div>
            )}
          </div>
          
          <div>
            <label className="cursor-pointer inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <FiUpload /> Upload New Logo
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleLogoUpload}
                disabled={loading}
              />
            </label>
            <p className="text-sm text-gray-500 mt-2">
              Recommended: 500x500px, PNG or JPG, max 2MB
            </p>
          </div>
        </div>
      </div>

      {/* Brand Colors Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Brand Colors</h2>
        
        <form onSubmit={handleUpdateGeneralSettings}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Color
              </label>
              <div className="flex items-center gap-3">
                <input 
                  type="color" 
                  value={settings.settings?.primary_color || '#3b82f6'}
                  onChange={(e) => setSettings({
                    ...settings,
                    settings: { ...settings.settings, primary_color: e.target.value }
                  })}
                  className="w-16 h-12 border-2 border-gray-300 rounded cursor-pointer"
                />
                <input 
                  type="text" 
                  value={settings.settings?.primary_color || '#3b82f6'}
                  onChange={(e) => setSettings({
                    ...settings,
                    settings: { ...settings.settings, primary_color: e.target.value }
                  })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary Color
              </label>
              <div className="flex items-center gap-3">
                <input 
                  type="color" 
                  value={settings.settings?.secondary_color || '#1e40af'}
                  onChange={(e) => setSettings({
                    ...settings,
                    settings: { ...settings.settings, secondary_color: e.target.value }
                  })}
                  className="w-16 h-12 border-2 border-gray-300 rounded cursor-pointer"
                />
                <input 
                  type="text" 
                  value={settings.settings?.secondary_color || '#1e40af'}
                  onChange={(e) => setSettings({
                    ...settings,
                    settings: { ...settings.settings, secondary_color: e.target.value }
                  })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          <button 
            type="submit"
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            disabled={loading}
          >
            <FiSave /> {loading ? 'Saving...' : 'Save Colors'}
          </button>
        </form>
      </div>

      {/* Banners Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Homepage Banners</h2>
          <button 
            onClick={() => setShowBannerModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus /> Add New Banner
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {banners.map((banner) => (
            <div key={banner.id} className="border border-gray-200 rounded-lg p-4 flex items-center gap-4">
              <img 
                src={banner.image} 
                alt={banner.title || 'Banner'}
                className="w-32 h-20 object-cover rounded"
              />
              
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{banner.title || 'Untitled Banner'}</h3>
                <p className="text-sm text-gray-600">{banner.subtitle}</p>
                {banner.link_url && (
                  <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                    {banner.link_url}
                  </a>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleBannerStatus(banner.id)}
                  className={`p-2 rounded ${banner.position > 0 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}
                  title={banner.position > 0 ? 'Active' : 'Inactive'}
                >
                  {banner.position > 0 ? <FiEye size={20} /> : <FiEyeOff size={20} />}
                </button>
                
                <button
                  onClick={() => handleDeleteBanner(banner.id)}
                  className="p-2 rounded bg-red-100 text-red-600 hover:bg-red-200"
                >
                  <FiTrash2 size={20} />
                </button>
              </div>
            </div>
          ))}

          {banners.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <FiImage size={48} className="mx-auto mb-4 opacity-50" />
              <p>No banners uploaded yet</p>
              <p className="text-sm">Click "Add New Banner" to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Banner Upload Modal */}
      {showBannerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Upload New Banner</h3>
              
              <form onSubmit={handleBannerUpload} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banner Image *
                  </label>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleBannerFileChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Recommended: 1920x600px, JPG or PNG, max 5MB
                  </p>
                  
                  {bannerPreview && (
                    <div className="mt-4">
                      <img src={bannerPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input 
                    type="text"
                    value={bannerForm.title}
                    onChange={(e) => setBannerForm({...bannerForm, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Enter banner title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subtitle
                  </label>
                  <textarea 
                    value={bannerForm.subtitle}
                    onChange={(e) => setBannerForm({...bannerForm, subtitle: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Enter banner subtitle"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Link URL
                    </label>
                    <input 
                      type="url"
                      value={bannerForm.link_url}
                      onChange={(e) => setBannerForm({...bannerForm, link_url: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button Text
                    </label>
                    <input 
                      type="text"
                      value={bannerForm.button_text}
                      onChange={(e) => setBannerForm({...bannerForm, button_text: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="Learn More"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button 
                    type="button"
                    onClick={() => {
                      setShowBannerModal(false);
                      setBannerPreview(null);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    disabled={loading}
                  >
                    <FiUpload /> {loading ? 'Uploading...' : 'Upload Banner'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandingSettings;

